// Pure error-normalization helpers (no Sentry import so they can be
// tested under bare Node, same approach as utils/sentryScrub.ts).
// Tested in tests/errors.test.ts.

/**
 * Shape of a Supabase PostgrestError / StorageError / AuthError: a plain
 * object (NOT an Error instance) with these keys. Passing one directly to
 * Sentry.captureException produces the useless
 * "Object captured as exception with keys: code, details, hint, message".
 */
export interface SupabaseErrorLike {
  message: string;
  code?: string | null;
  details?: string | null;
  hint?: string | null;
}

export function isSupabaseErrorLike(err: unknown): err is SupabaseErrorLike {
  if (err == null || typeof err !== 'object' || err instanceof Error) return false;
  const e = err as Record<string, unknown>;
  return (
    typeof e.message === 'string' &&
    ('code' in e || 'details' in e || 'hint' in e)
  );
}

const ABORT_PATTERN = /\babort(ed|error)?\b/i;

/**
 * True for cancelled/aborted requests (app backgrounded, navigation away,
 * fetch timeout abort…). These are transient and not actionable — they
 * should neither be reported to Sentry nor wipe local state.
 *
 * Supabase wraps the underlying fetch AbortError into a PostgrestError whose
 * message/details contain "AbortError: Aborted", so we check those too.
 */
/**
 * True for a used or timed-out email OTP link (signup, recovery, email
 * change). The user can still sign in or request a new mail; this is not an
 * application defect and must not page Sentry.
 */
export function isExpectedAuthLinkError(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false;
  const e = err as { message?: unknown; code?: unknown };
  if (e.code === 'otp_expired') return true;
  return typeof e.message === 'string' &&
    /email link is invalid or has expired|token has expired or is invalid/i.test(e.message);
}

export function isAbortError(err: unknown): boolean {
  if (err == null || typeof err !== 'object') return false;
  const e = err as Record<string, unknown>;
  if (e.name === 'AbortError') return true;
  if (typeof e.message === 'string' && ABORT_PATTERN.test(e.message)) return true;
  if (typeof e.details === 'string' && ABORT_PATTERN.test(e.details)) return true;
  return false;
}

/**
 * Convert anything thrown into a real Error instance so Sentry shows a
 * proper type/message instead of "Object captured as exception".
 */
export function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (isSupabaseErrorLike(err)) {
    const error = new Error(err.message);
    error.name = 'SupabaseError';
    return error;
  }
  if (typeof err === 'string') return new Error(err);
  try {
    return new Error(`Non-Error thrown: ${JSON.stringify(err)}`);
  } catch {
    return new Error('Non-Error thrown (unserializable)');
  }
}
